import { Component, input, model, output, OnInit, signal, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { FormMode } from 'app/core/constants/form-mode';
import { Global } from '@services/core/Global';
import Swal from 'sweetalert2';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { inject } from '@angular/core';

@Component({
  selector: 'app-document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, NgSelectModule, CommonModule],
})
export class DocumentFormComponent implements OnInit {

  documentForm = input.required<FormGroup>();
  document = input.required<any>();
  mode = model(FormMode.EDIT);
  partyOverviewService = input<any>();
  /** true = optimiza y sube al servidor (claim detalle). false = solo deja el archivo en el form. */
  immediateUpload = input(false);
  compact = input(false);
  onUpdate = output<void>();
  onImageClick = output<string | number>();
  onUploadFile = output<{ uuid: string; file: File; origin?: string; expirationDate?: string | null; done?: (ok: boolean) => void }>();
  onDelete = output<string>();

  formMode = FormMode;
  previewUrl = signal<string | null>(null);
  isImage = false;
  baseUrl = Global.url;
  private imageOptimizer = inject(ImageOptimizerService);
  isEditing = signal<boolean>(false);
  /** Optimización / envío de ESTE documento (no bloquea los demás). */
  isBusy = signal(false);
  busyLabel = signal('Procesando...');
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  pickFile(): void {
    if (this.isBusy() || this.mode() === this.formMode.VIEW) return;
    this.fileInput?.nativeElement.click();
  }

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2200,
    timerProgressBar: true
  });

  ngOnInit(): void {
    this.documentForm().patchValue({
      processSlug: this.document().processSlug,
      ownerSlug: this.document().ownerSlug,
      categorySlug: this.document().categorySlug,
      typeSlug: this.document().typeSlug,
      partyId: this.document().partyId,
    });
    const hasFile = !!this.document()?.filePath || !!this.documentForm().get('file')?.value;
    this.isEditing.set(this.mode() === this.formMode.EDIT && !hasFile);
  }

  /** Orquesta el flujo de selección de archivos */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.isValidInput(input) || this.isBusy()) return;
    this.processSelectedFile(input.files![0]);
    input.value = '';

    // Con vencimiento: esperar fecha + Guardar (no subir solo al elegir archivo)
    if (this.document()?.allowExpiration) return;

    if (this.partyOverviewService()) {
      void this.processAndSaveDocument();
    } else if (this.immediateUpload()) {
      void this.processAndEmitUpload();
    } else {
      void this.optimizeInPlace();
    }
  }

  /** Optimiza en segundo plano y deja el archivo en el form (envío diferido). */
  private async optimizeInPlace(): Promise<void> {
    const file = this.documentForm().get('file')?.value as File;
    if (!file || !this.isImageFile(file)) return;
    this.isBusy.set(true);
    this.busyLabel.set('Optimizando...');
    try {
      const optimized = await this.getFinalFile(file);
      this.updateFormState(optimized);
      this.setPreviewImage(optimized);
    } finally {
      this.isBusy.set(false);
    }
  }

  private isValidInput(input: HTMLInputElement): boolean {
    return !!(input.files && input.files.length);
  }

  private processSelectedFile(file: File): void {
    if (this.isImageFile(file)) {
      this.processImageFile(file);
    } else {
      this.processStandardFile(file);
    }
  }

  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private processImageFile(file: File): void {
    this.updateFormState(file);
    this.setPreviewImage(file);
  }

  private processStandardFile(file: File): void {
    this.updateFormState(file);
    this.setPreviewDocument();
  }

  private updateFormState(file: File): void {
    this.documentForm().patchValue({
      file: file,
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size
    });
    this.markFormAsDirty();
  }

  private markFormAsDirty(): void {
    this.documentForm().markAsDirty();
    this.documentForm().markAsTouched();
  }

  private setPreviewImage(file: File): void {
    this.isImage = true;
    const reader = new FileReader();
    reader.onload = () => {
      this.previewUrl.set(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  private setPreviewDocument(): void {
    this.isImage = false;
    this.previewUrl.set('pdf');
  }

  editMode() {
    if (this.isBusy()) return;
    this.isEditing.set(true);
  }

  async saveDocument() {
    if (!this.documentForm().get('file')?.value) {
      this.Toast.fire({ icon: 'warning', title: 'Debe seleccionar un archivo' });
      return;
    }
    if (this.document()?.allowExpiration && !this.documentForm().get('expirationDate')?.value) {
      this.Toast.fire({ icon: 'warning', title: 'Debe indicar la fecha de vencimiento' });
      return;
    }

    if (this.partyOverviewService()) {
      await this.processAndSaveDocument();
    } else {
      await this.processAndEmitUpload();
    }
  }

  private async processAndEmitUpload() {
    const fileToUpload = this.documentForm().get('file')?.value as File;
    this.isBusy.set(true);
    this.busyLabel.set(this.isImage ? 'Optimizando...' : 'Preparando...');
    try {
      const finalFile = await this.getFinalFile(fileToUpload);
      this.busyLabel.set('Subiendo...');
      await new Promise<void>((resolve) => {
        this.onUploadFile.emit({
          uuid: this.document().uuid,
          file: finalFile,
          origin: this.document()?.origin,
          expirationDate: this.documentForm().get('expirationDate')?.value || null,
          done: (ok) => {
            if (ok) {
              this.resetFormAfterUpload();
            }
            resolve();
          }
        });
      });
    } catch {
      this.Toast.fire({ icon: 'error', title: 'No se pudo preparar el archivo' });
    } finally {
      this.isBusy.set(false);
    }
  }

  private async getFinalFile(file: File): Promise<File> {
    if (!this.isImage) return file;
    try {
      const optimized = await this.imageOptimizer.optimize(file);
      return optimized || file;
    } catch {
      return file;
    }
  }

  private resetFormAfterUpload() {
    this.isEditing.set(false);
    this.previewUrl.set(null);
    this.documentForm().patchValue({ file: null });
  }

  private async processAndSaveDocument() {
    const fileToUpload = this.documentForm().get('file')?.value as File;
    const formData = new FormData();
    const expirationDate = this.documentForm().get('expirationDate')?.value;
    if (this.document()?.allowExpiration && !expirationDate) {
      this.Toast.fire({ icon: 'warning', title: 'Debe indicar la fecha de vencimiento' });
      return;
    }

    this.isBusy.set(true);
    this.busyLabel.set(this.isImage ? 'Optimizando imagen...' : 'Subiendo...');
    try {
      if (this.isImage) {
        try {
          const optimizedFile = await this.imageOptimizer.optimize(fileToUpload);
          formData.append('file', optimizedFile || fileToUpload);
        } catch {
          formData.append('file', fileToUpload);
        }
        this.busyLabel.set('Subiendo...');
      } else {
        formData.append('file', fileToUpload);
      }
      if (expirationDate) formData.append('expirationDate', expirationDate);

      this.partyOverviewService().updateDocument(this.document().uuid, formData).subscribe({
        next: () => {
          this.isBusy.set(false);
          this.Toast.fire({ icon: 'success', title: 'Documento actualizado' });
          this.isEditing.set(false);
          this.onUpdate.emit();
        },
        error: (err) => {
          this.isBusy.set(false);
          this.Toast.fire({ icon: 'error', title: err?.error?.message || 'Error al actualizar' });
        }
      });
    } catch {
      this.isBusy.set(false);
      this.Toast.fire({ icon: 'error', title: 'Error al preparar el archivo' });
    }
  }

  cancelEdit() {
    if (this.isBusy()) return;
    this.isEditing.set(false);
    this.previewUrl.set(null);
    this.documentForm().patchValue({ file: null });
  }

  deleteDocument() {
    if (this.isBusy()) return;
    const swalOpts: any = {
      title: '¿Eliminar documento?',
      text: 'Se quitará el requisito.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí'
    };
    Swal.fire(swalOpts).then(r => r.isConfirmed && this.onDelete.emit(this.document().uuid));
  }

  onExpirationDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value || null;
    this.documentForm().patchValue({ expirationDate: value });
    this.documentForm().get('expirationDate')?.markAsDirty();
  }
}
